import { STATUS, MAP_STATUS_COLOR } from 'Constants'

/**
 * Checks whether a timestamp represents a real, set date.
 * The backend (Go) serialises unset times as the zero value
 * "0001-01-01T00:00:00Z", which would otherwise render as
 * "01 January 0001". Treat those (and any unparseable value) as invalid.
 */
export const isValidTimestamp = (timestamp) => {
  if (!timestamp) return false
  const date = new Date(timestamp)
  if (Number.isNaN(date.getTime())) return false
  // Anything at or before year 1 is the Go zero value / not a real date.
  if (date.getUTCFullYear() <= 1) return false
  return true
}

// Derives a human label for a status, falling back to a generated key when the
// status isn't present in the STATUS map.
const labelForStatus = (status) =>
  STATUS[status]?.label ||
  (status ? `summary${status.charAt(0).toUpperCase()}${status.slice(1)}` : 'summarySubmitted')

const colorForStatus = (status) => MAP_STATUS_COLOR[status] || '#EB7C0C'

/**
 * Builds a timeline from the backend's real status-change events.
 * Each event carries the exact time the transition happened, so this is the
 * accurate source of truth (as opposed to reconstructing from updated_at).
 *
 * Accepts events in either the raw API shape (`created_at`) or the formatted
 * shape, and both snake_case (`status_events`) and camelCase (`statusEvents`).
 */
const buildFromStatusEvents = (events) => {
  if (!events?.length) return []

  return events
    .filter((e) => e && isValidTimestamp(e.created_at ?? e.createdAt ?? e.timestamp))
    .map((e) => ({
      status: e.status,
      label: labelForStatus(e.status),
      color: colorForStatus(e.status),
      timestamp: e.created_at ?? e.createdAt ?? e.timestamp,
    }))
}

const sortByTimestampDesc = (history) => {
  const seen = new Set()
  const unique = history.filter((h) => {
    const key = `${h.status}-${h.timestamp}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })

  unique.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
  return unique
}

/**
 * Builds a status history timeline from a (formatted) summary.
 *
 * Prefers the backend's real per-status-change events when available; otherwise
 * falls back to the best-effort reconstruction from scattered timestamps.
 */
export const buildStatusHistory = (summary) => {
  if (!summary) return []

  // Preferred: real status-change events from the backend.
  const events = summary.statusEvents || summary.status_events
  if (events?.length) {
    return sortByTimestampDesc(buildFromStatusEvents(events))
  }

  // Fallback: reconstruct from available timestamps (legacy, approximate).
  const history = []

  if (summary.statusTime) {
    history.push({
      status: summary.status || 'submitted',
      label: summary.formattedStatus?.label || labelForStatus(summary.status),
      color: summary.formattedStatus?.color || colorForStatus(summary.status),
      timestamp: summary.statusTime,
    })
  }

  if (summary.advice?.date && isValidTimestamp(summary.advice.date)) {
    history.push({
      status: 'adviceSent',
      label: STATUS.adviceSent?.label || 'summaryAdviceSent',
      color: MAP_STATUS_COLOR.adviceSent || '#9B2A3B',
      timestamp: summary.advice.date,
    })
  }

  return sortByTimestampDesc(history)
}

/**
 * Builds status history from a registration object (CS_m.ModelsRegistration).
 * Used in the patient detail view where we have access to the full registration.
 */
export const buildStatusHistoryFromRegistration = (registration) => {
  if (!registration) return []

  const history = []
  const summaries = registration.summaries || []

  // Registration created
  if (registration.created_at) {
    history.push({
      status: 'registered',
      label: 'summaryRegistered',
      color: '#EB7C0C',
      timestamp: registration.created_at,
    })
  }

  summaries.forEach((summary) => {
    // Preferred: real per-status-change events from the backend. When present,
    // these replace the approximate reconstruction below for the summary's
    // own status transitions.
    const events = summary.status_events || summary.statusEvents
    if (events?.length) {
      history.push(...buildFromStatusEvents(events))
    } else {
      // Fallback reconstruction from scattered timestamps (legacy, approximate).

      // Summary created / submitted
      if (summary.updated_at) {
        history.push({
          status: summary.status,
          label: labelForStatus(summary.status),
          color: colorForStatus(summary.status),
          timestamp: summary.updated_at,
        })
      }

      // Consulted timestamp
      if (summary.consulted_at && isValidTimestamp(summary.consulted_at)) {
        history.push({
          status: 'consulted',
          label: STATUS.consulted?.label || 'summaryConsulted',
          color: MAP_STATUS_COLOR.consulted || '#33b897',
          timestamp: summary.consulted_at,
        })
      }

      // Advice sent
      if (summary.advice?.created_at_datetime && isValidTimestamp(summary.advice.created_at_datetime)) {
        history.push({
          status: 'adviceSent',
          label: STATUS.adviceSent?.label || 'summaryAdviceSent',
          color: MAP_STATUS_COLOR.adviceSent || '#9B2A3B',
          timestamp: summary.advice.created_at_datetime,
        })
      }

      // Referral sent
      if (summary.referral?.url) {
        history.push({
          status: 'referralSent',
          label: STATUS.referralSent?.label || 'summaryReferralSent',
          color: MAP_STATUS_COLOR.referralSent || '#9B2A3B',
          timestamp: summary.updated_at,
        })
      }
    }

    // Appointment statuses (genuine created_at/updated_at, kept regardless).
    const appointments = summary.appointments || []
    appointments.forEach((appt) => {
      if (appt.status && appt.updated_at) {
        const apptStatusObj = STATUS[appt.status]
        history.push({
          status: appt.status,
          label: apptStatusObj?.label || appt.status,
          color: MAP_STATUS_COLOR[appt.status] || '#EB7C0C',
          timestamp: appt.updated_at,
        })
      }
      if (appt.created_at && appt.status) {
        history.push({
          status: 'pending',
          label: STATUS.pending?.label || 'summaryPending',
          color: MAP_STATUS_COLOR.pending || '#EB7C0C',
          timestamp: appt.created_at,
        })
      }
    })
  })

  // Deduplicate and sort newest first
  const seen = new Set()
  const unique = history.filter((h) => {
    const key = `${h.label}-${h.timestamp}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })

  unique.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))

  return unique
}
